/**
 * ログインページ - Nexus Core Design System (AIRGROW)
 */

import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useFadeNavigation } from '../hooks/useFadeNavigation';
import { Input } from '../components/ui';
import AuthLayout from '../components/auth/AuthLayout';

const MaterialIcon = ({ name, className = '', filled = false, style = {} }) => (
  <span
    className={`material-symbols-outlined ${className}`}
    style={{ fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0", ...style }}
  >
    {name}
  </span>
);

const VisibilityToggle = ({ visible, onToggle }) => (
  <button
    type="button"
    tabIndex={-1}
    onClick={onToggle}
    className="flex items-center text-outline hover:text-text-primary transition-colors focus:outline-none"
  >
    <MaterialIcon name={visible ? 'visibility_off' : 'visibility'} className="text-[18px]" />
  </button>
);

export default function Login() {
  const { login, commitLogin, setNewPassword, commitNewPassword, pendingUser, error } = useAuth();
  const navigate = useNavigate();
  const { cardVisible, isLeaving, fadeOutThen, navigateWithFade } = useFadeNavigation();
  const { collapsing: isCollapsing, setCollapsing } = useOutletContext();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [newPassword, setNewPasswordVal] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const COLLAPSE_DURATION_MS = 600;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // カードをツールバー幅に縮めるアニメーション → 完了後に commitLogin → navigate
        setCollapsing(true);
        setTimeout(() => {
          commitLogin(result.email);
          navigate('/');
        }, COLLAPSE_DURATION_MS);
      }
    } catch (err) {
      setLocalError(err.message || 'ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError('パスワードが一致しません');
      return;
    }

    if (newPassword.length < 8) {
      setLocalError('パスワードは8文字以上で入力してください');
      return;
    }

    setIsLoading(true);
    try {
      const result = await setNewPassword(newPassword);
      if (result.success) {
        fadeOutThen(async () => {
          await commitNewPassword();
          navigate('/');
        });
      }
    } catch (err) {
      setLocalError(err.message || 'パスワード変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 初回パスワード変更画面
  if (pendingUser) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <div className="flex flex-col items-center mb-10">
          <div className="w-12 h-12 bg-nexus-primary flex items-center justify-center rounded-lg mb-4 shadow-lg shadow-nexus-primary/20">
            <MaterialIcon name="key" filled className="text-white text-[28px]" />
          </div>
          <h1 className="text-[24px] leading-[32px] font-bold text-text-primary tracking-tight">パスワード変更</h1>
          <p className="text-sm text-text-secondary mt-1 text-center">
            初回ログインのため、新しいパスワードを設定してください
          </p>
        </div>

        <form onSubmit={handleNewPassword} className="space-y-6">
          {(localError || error) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {localError || error}
            </div>
          )}

          <Input
            id="new-password"
            label="新しいパスワード"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPasswordVal(e.target.value)}
            placeholder="8文字以上、大小英字・数字含む"
            required
            icon={<MaterialIcon name="lock" className="text-[18px]" />}
            iconRight={<VisibilityToggle visible={showNewPassword} onToggle={() => setShowNewPassword((v) => !v)} />}
          />

          <Input
            id="confirm-password"
            label="パスワード確認"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="パスワード再入力"
            required
            icon={<MaterialIcon name="lock" className="text-[18px]" />}
            iconRight={<VisibilityToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword((v) => !v)} />}
          />

          <button
            type="submit"
            disabled={isLoading || isLeaving}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? '変更中...' : 'パスワードを変更'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  // 通常ログイン画面
  return (
    <AuthLayout cardVisible={cardVisible}>
      {/* Logo & Header (collapsing 時はアイコンのみ残し、サイズ縮小・グラデーション化) */}
      <div
        className={`flex flex-col items-center transition-[margin] duration-[600ms] ease-in-out ${
          isCollapsing ? 'mb-0' : 'mb-10'
        }`}
      >
        <div
          className={`flex items-center justify-center rounded-lg shadow-lg shadow-nexus-primary/20 transition-all duration-[600ms] ease-in-out ${
            isCollapsing
              ? 'w-10 h-10 bg-gradient-to-b from-inverse-primary to-nexus-primary'
              : 'w-12 h-12 bg-nexus-primary mb-4'
          }`}
        >
          <MaterialIcon
            name="rocket_launch"
            filled
            className={`text-white transition-all duration-[600ms] ease-in-out ${
              isCollapsing ? 'text-[22px]' : 'text-[28px]'
            }`}
          />
        </div>
        <h1
          className={`text-[24px] leading-[32px] font-bold text-text-primary tracking-tight whitespace-nowrap transition-opacity duration-[300ms] ${
            isCollapsing ? 'opacity-0' : 'opacity-100'
          }`}
        >
          AIRGROW
        </h1>
        <p
          className={`text-sm text-text-secondary mt-1 whitespace-nowrap transition-opacity duration-[300ms] ${
            isCollapsing ? 'opacity-0' : 'opacity-100'
          }`}
        >
          Autonomous Fleet Management
        </p>
      </div>

      {/* Login Form (collapsing 時はフェードアウト + クリック無効化) */}
      <form
        onSubmit={handleLogin}
        className={`space-y-6 transition-opacity duration-[300ms] ${
          isCollapsing ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {(localError || error) && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {localError || error}
          </div>
        )}

        <Input
          id="email"
          label="メールアドレス"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.com"
          required
          autoFocus
          icon={<MaterialIcon name="mail" className="text-[18px]" />}
        />

        <Input
          id="password"
          label="パスワード"
          labelRight={
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="text-xs text-nexus-primary hover:underline font-medium"
            >
              パスワードを忘れた方
            </a>
          }
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          icon={<MaterialIcon name="lock" className="text-[18px]" />}
          iconRight={<VisibilityToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />}
        />

        {/* Remember device */}
        <div className="flex items-center">
          <input
            id="remember"
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            className="h-4 w-4 text-nexus-primary border-border-light rounded focus:ring-nexus-primary transition-all cursor-pointer"
          />
          <label htmlFor="remember" className="ml-2 block text-xs text-text-secondary cursor-pointer">
            このデバイスを30日間記憶する
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || isLeaving}
          className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? 'ログイン中...' : 'ログイン'}
        </button>

        <div className="mt-4 text-center text-xs">
          <span className="text-text-secondary">アカウントをお持ちでない方は </span>
          <a
            href="/register"
            onClick={(e) => {
              e.preventDefault();
              if (!isLeaving) navigateWithFade('/register');
            }}
            className="text-nexus-blue hover:underline font-medium"
          >
            新規登録
          </a>
        </div>
      </form>
    </AuthLayout>
  );
}
